import styled, { css } from 'styled-components';
import { hexToRGB } from '@/styles/helpers';

interface IScProgressBarProps {
  $issued?: boolean;
  $percent?: number;
  $variant?: 'pill';
}

export const ScProgressBar = styled.div<IScProgressBarProps>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  width: 100%;
  margin-bottom: 4px;

  .divider {
    width: 100%;
    max-width: 704px;
    height: 7px;
    background: ${({ theme }) => hexToRGB(theme.whiteColor, 0.2)};
    clip-path: polygon(
      0 0,
      16.16% 0,
      17.62% 85.7%,
      85.72% 85.7%,
      87.18% 0,
      100% 0,
      100% 1px,
      87.18% 1px,
      85.72% 100%,
      17.62% 100%,
      16.16% 1px,
      0 1px
    );
  }

  .progressMeter {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    width: 100%;
    max-width: 307px;
  }

  .progressTrack {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 14px;
    background: ${({ $issued }) =>
      $issued ? 'rgba(147, 219, 55, 0.4)' : 'rgba(37, 170, 214, 0.4)'};
    overflow: hidden;
  }

  .progressFill {
    position: absolute;
    inset: 0 auto 0 0;
    height: 100%;
    background: ${({ $issued }) => ($issued ? '#93db37' : '#4ec4eb')};
    box-shadow: 0 0 2px 0 rgba(112, 192, 206, 0.5);
    transition: width 0.3s ease;
  }

  .levelBadge {
    position: relative;
    font-weight: 700;
    font-size: 12px;
    line-height: 1;
    z-index: 1;
    color: ${({ $issued, $percent = 0 }) =>
      $issued || $percent >= 50 ? '#0d0d0d' : '#4ec4eb'};
    text-shadow: ${({ $issued, $percent = 0 }) =>
      !$issued && $percent < 50 ? '0 0 3px rgba(0, 0, 0, 0.8)' : 'none'};
  }

  .statsRow {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    justify-content: space-between;
    gap: 4px 12px;
    width: 100%;
  }

  .statBlock {
    display: flex;
    flex-direction: column;
    align-items: flex-start;

    &.right {
      align-items: flex-end;
      margin-left: auto;
      text-align: right;
    }
  }

  .statValue {
    font-weight: 700;
    font-size: 14px;
    line-height: 18px;
    letter-spacing: 0.14px;
    text-transform: uppercase;
    color: ${({ theme }) => theme.whiteColor};

    > span {
      color: ${({ $issued }) => ($issued ? '#93db37' : '#4ec4eb')};
    }
  }

  .statCaption {
    font-size: 12px;
    line-height: 14px;
    color: #c1c2c5;
  }

  ${({ $variant }) => $variant === 'pill' && progressBarPillVariant}
`;

const progressBarPillVariant = css`
  gap: 16px;

  .divider {
    clip-path: none;
    height: 1px;
    background: radial-gradient(
      50% 50% at 50% 50%,
      #d9d9d9 0%,
      rgba(115, 115, 115, 0) 100%
    );
  }

  .progressTrack {
    height: 24px;
    background: white;
    border-radius: 999px;

    &.full {
      .progressFill {
        border-radius: ${({ theme }) => theme.borderRadius1};
        background: ${({ theme }) => theme.freshLimeGradint};
      }
    }
  }

  .progressFill {
    border: 2px solid ${({ theme }) => theme.whiteColor};
    border-radius: 999px 0 0 999px;
    background: linear-gradient(90deg, #ffad01 20%, #ffd500 80%);
    box-shadow: none;
  }

  .levelBadge {
    font-size: 14px;
    line-height: 20px;
    letter-spacing: 0.14px;
    color: #0d0d0d;
    text-shadow: none;
  }

  .statValue > span {
    color: #ea5b0c;
  }
`;
